import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { FormEvent, KeyboardEvent, MouseEvent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import SurpriseButton, { SurpriseTextsType } from "../../../../../../components/common/SurpriseButton/SurpriseButton";
import styles from "./TgPromptInput.module.scss";
import TextToImageError from "../../../../../Playground/TextToImage/Generator/Search/Error/TextToImageError";
import { useAppStore } from "../../../../../../hooks/useStores";
import { APPS } from "../../../../../../constants/AppsConstants";
import { TG_PROMPT_INPUT_LIGHT_HEIGHT } from "../../../../../../constants/TextToImageConstants";

type Props = {
	prefix?: string;
	placeholder?: string;
	prompt?: string;
	onChange?: (newPrompt: string) => void;
	onEnter?: (e: KeyboardEvent<HTMLSpanElement>) => void;
	surpriseTextsList?: SurpriseTextsType;
	disabled?: boolean;
	className?: string;
	fontSize?: string;
	maxRows?: number;
};

const TgPromptInput = ({
	prefix = "",
	prompt = "",
	placeholder = "",
	onChange,
	onEnter,
	surpriseTextsList,
	disabled,
	className,
	fontSize,
	maxRows = 8,
}: Props) => {
	const { t } = useTranslation("translation", { keyPrefix: "newTg.app.promptInput" });
	const inputRef = useRef<HTMLSpanElement>(null);
	const textPromptRef = useRef(null);
	const { textToImageStore, playgroundStore } = useAppStore();
	const isNewTg = playgroundStore.selectedConfig === APPS.TG_IMAGE_GENERATION;
	const hideError = textToImageStore.errors?.generate?.status === 422;

	const updateInputHeight = () => {
		if (!inputRef.current) return;
		const el = inputRef.current;
		el.style.height = "auto";
		const scrollHeight = el.scrollHeight;
		const maxHeight = TG_PROMPT_INPUT_LIGHT_HEIGHT * maxRows;
		el.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
	};

	useEffect(() => {
		if (!inputRef.current) return;

		if (disabled) {
			inputRef.current.innerText = prompt || "";
			return;
		}

		if (!prompt) {
			inputRef.current.innerText = " ";
		}

		updateInputHeight();
	}, [prompt, disabled]);

	const handleFocus = () => {
		if (disabled) return;
		inputRef.current?.focus();
	};

	const handleInput = (e: FormEvent<HTMLSpanElement>) => {
		if (disabled) return;
		const rawText = e.currentTarget.innerText;
		onChange?.(rawText.trim());
		updateInputHeight();
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
		if (disabled) return;
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			onEnter?.(e);
		}
	};

	const handleSurpriseClick = (_e: MouseEvent<HTMLButtonElement>, surprisedText: string) => {
		onChange?.(surprisedText);
		if (inputRef.current) {
			inputRef.current.innerText = surprisedText || " ";
			inputRef.current.focus();
			updateInputHeight();
		}
	};

	return (
		<Box className={clsx(styles.inputWrapper, { [styles.disabled]: disabled, [styles.sideInput]: maxRows === 8 }, className)} onClick={handleFocus} ref={textPromptRef}>
			{isNewTg && !hideError && (
				<TextToImageError anchorRef={textPromptRef} containerRef={textToImageStore.imageGenerationContainerRef} />
			)}
			<Box className={styles.prompt} sx={{ maxHeight: `${TG_PROMPT_INPUT_LIGHT_HEIGHT * maxRows}px` }}>
				<span className={styles.prefixLine}>
					{!disabled && (
						<span className={styles.prefixTag} style={{ fontSize }}>
							{t("prefix.tag")}
						</span>
					)}
					{prefix ? (
						<span className={styles.prefixText} style={{ fontSize }}>
							{prefix}
						</span>
					) : (
						<span className={styles.inputPrompt} style={{ fontSize }} data-placeholder={placeholder} />
					)}
					<span
						ref={inputRef}
						className={styles.inputPrompt}
						style={{ fontSize, maxHeight: `${TG_PROMPT_INPUT_LIGHT_HEIGHT * maxRows}px !important` }}
						contentEditable={!disabled}
						tabIndex={disabled ? -1 : 0}
						suppressContentEditableWarning
						onInput={handleInput}
						onKeyDown={handleKeyDown}
						dir="ltr"
					/>
				</span>
			</Box>

			{surpriseTextsList && (
				<Box className={styles.footer}>
					<Typography className={styles.footerText}>{t("footer")}</Typography>
					<SurpriseButton
						className={styles.surpriseBtn}
						buttonType="textSmall"
						textsListType={surpriseTextsList}
						onSurprise={handleSurpriseClick}
					/>
				</Box>
			)}
		</Box>
	);
};

export default observer(TgPromptInput);
